"use client";

import { useState, useEffect, forwardRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { __permissionsObj } from "./constants/index";
import { getAdminProfileController } from "./Controllers/Auth";
import MainState from "./contexts/MainState";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import LoginPage from "./Components/Login/LoginPage.jsx";
import ResetPassword from "./Components/Login/ResetPassword";
import HomePage from "./Components/Home/HomePage";
import Notifications from "./Components/Notification/Notification";
import SendNotification from "./Components/Notification/SendNotification.jsx";
import ScheduledNotifications from "./Components/Notification/ScheduledNotifications";

import Dashboard from "./Components/Dashboard/Dashboard";
import SeekersListing from "./Components/Users/SeekersListing";
import HouseKeepersListing from "./Components/Users/HouseKeepersListing";
import BabySittersListing from "./Components/Users/BabySittersListing";

import SeekerNeeds from "./Components/AppManagement/AppManagementSeeker/SeekerNeeds";

import HouseKeeperAvailableFor from "./Components/AppManagement/AppManagementHouseKeeper/HouseKeeperAvailableFor";
import HouseKeeperExperience from "./Components/AppManagement/AppManagementHouseKeeper/HouseKeeperExperience";
import HouseKeeperHappyWith from "./Components/AppManagement/AppManagementHouseKeeper/HouseKeeperHappyWith";
import HouseKeeperIHaveMyOwn from "./Components/AppManagement/AppManagementHouseKeeper/HouseKeeperIHaveMyOwn";

import BabySitterAvailableFor from "./Components/AppManagement/AppManagementBabySitter/BabySitterAvailableFor";
import BabySitterHappyWith from "./Components/AppManagement/AppManagementBabySitter/BabySitterHappyWith";
import BabySitterHave from "./Components/AppManagement/AppManagementBabySitter/BabySitterHave";
import BabySitterSpecialties from "./Components/AppManagement/AppManagementBabySitter/BabySitterSpecialties";
import BabySitterExperience from "./Components/AppManagement/AppManagementBabySitter/BabySitterExperience";
import BabySitterChildrence from "./Components/AppManagement/AppManagementBabySitter/BabySitterChildrence";

import JobPosted from "./Components/BookingManagement/JobPosted";
import Bookings from "./Components/BookingManagement/Bookings";

import SubAdmin from "./Components/RoleManagement/SubAdmin";

import Cities from "./Components/ScopeManagement/Cities";
import NonaFees from "./Components/ScopeManagement/NonaFees";
import JobAppliersListing from "./Components/BookingManagement/JobAppliersListing";
import PolicyPage from "./Components/GeneralSettings/PolicyPage";
import TermsAndConditions from "./Components/GeneralSettings/TermsAndConditions";
import Disclaimer from "./Components/GeneralSettings/Disclaimer";
import ReviewListing from "./Components/Users/ReviewListing";
import Promocode from "./Components/ScopeManagement/Promocode";
import Roles from "./Components/RoleManagement/Roles";
import Categories from "./Components/AppManagement/Help&Support/Categories";
import HelpSupportList from "./Components/Help&Suppport/HelpSupportList";
import ChatLogs from "./Components/Users/Chatlogs/ChatLogs";
import ReportList from "./Components/Reports/ReportList";
import UserPayments from "./Components/Payments/UserPayments";
import WithdrawRequest from "./Components/Payments/WithdrawRequest";
import Areas from "./Components/ScopeManagement/Areas";
import ReturnUrl from "./Components/StripeWebPage/ReturnUrl";
import RefreshUrl from "./Components/StripeWebPage/RefreshUrl";
import CancelPolicy from "./Components/GeneralSettings/CancelPolicy";
import FAQs from "./Components/FAQs/FAQs";
import CustomerFAQs from "./Components/FAQs/CustomerFAQs";
import HouseKeeperFAQs from "./Components/FAQs/HouseKeeperFAQs";
import BabySitterFAQs from "./Components/FAQs/BabySitterFAQs";
import DisputesList from "./Components/InvoiceDisputes/DisputesList";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

function App() {
  const [splashCompleted, setSplashCompleted] = useState(true);
  const [loginUserDetails, setLoginUserDetails] = useState({});

  const [snackBar, setSnackBar] = useState({
    status: false,
    text: "",
    severity: "",
  });

  const getAdminProfile = async (token) => {
    const res = await getAdminProfileController(token);
    if (res?.data?.status === 200) {
      setLoginUserDetails(true);
      setLoginUserDetails(res.data.data);
    }
  };

  useEffect(() => {
    getAdminProfile();
  }, []);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Snackbar
          open={snackBar.status}
          autoHideDuration={3000}
          onClose={() => {
            setSnackBar({ status: false, text: "", severity: "" });
          }}
        >
          <Alert
            severity={snackBar.severity}
            sx={{ width: "100%", fontFamily: "regular" }}
          >
            {" "}
            {snackBar.text}{" "}
          </Alert>
        </Snackbar>
        <MainState
          loginUserDetails={loginUserDetails}
          setLoginUserDetails={setLoginUserDetails}
          snackBar={snackBar}
          setSnackBar={setSnackBar}
        >
          <BrowserRouter>
            {Object.keys(loginUserDetails).length > 0 ? (
              <Routes>
                <Route
                  path="/login"
                  element={<LoginPage getAdminProfile={getAdminProfile} />}
                />
                <Route
                  path="/"
                  element={
                    <HomePage
                      loginUserDetails={loginUserDetails}
                      setLoginUserDetails={setLoginUserDetails}
                    />
                  }
                >
                  {loginUserDetails.managerPermission.includes(
                    __permissionsObj.Dashboard
                  ) || loginUserDetails.role === "Admin" ? (
                    <Route index element={<Dashboard />} />
                  ) : (
                    <></>
                  )}
                  <Route path="Notification" element={<Notifications />} />
                  <Route
                    path="notifications/send"
                    element={<SendNotification />}
                  />
                  <Route
                    path="notifications/scheduled"
                    element={<ScheduledNotifications />}
                  />
                  {loginUserDetails.managerPermission.includes(
                    __permissionsObj.Seeker
                  ) || loginUserDetails.role === "Admin" ? (
                    <Route
                      path="users/seekers-listing"
                      element={<SeekersListing />}
                    />
                  ) : (
                    <></>
                  )}
                  {loginUserDetails.managerPermission.includes(
                    __permissionsObj.BabySitter
                  ) || loginUserDetails.role === "Admin" ? (
                    <Route
                      path="users/baby-sitters-listing"
                      element={<BabySittersListing />}
                    />
                  ) : (
                    <></>
                  )}
                  {loginUserDetails.managerPermission.includes(
                    __permissionsObj.HouseKeeper
                  ) || loginUserDetails.role === "Admin" ? (
                    <Route
                      path="users/house-keepers-listing"
                      element={<HouseKeepersListing />}
                    />
                  ) : (
                    <></>
                  )}
                  {loginUserDetails.role === "Admin" ? (
                    <Route
                      path="users/review-listing"
                      element={<ReviewListing />}
                    />
                  ) : (
                    <></>
                  )}
                  {loginUserDetails.managerPermission.includes(
                    __permissionsObj.HouseKeeper
                  ) || loginUserDetails.role === "Admin" ? (
                    <Route path="users/chat-logs" element={<ChatLogs />} />
                  ) : (
                    <></>
                  )}
                  {loginUserDetails.managerPermission.includes(
                    __permissionsObj.AppManagement
                  ) || loginUserDetails.role === "Admin" ? (
                    <>
                      <Route
                        path="app/seeker/needs"
                        element={<SeekerNeeds />}
                      />

                      <Route
                        path="app/house-keeper/available-for"
                        element={<HouseKeeperAvailableFor />}
                      />
                      <Route
                        path="app/house-keeper/happy-with"
                        element={<HouseKeeperHappyWith />}
                      />
                      <Route
                        path="app/house-keeper/experience"
                        element={<HouseKeeperExperience />}
                      />
                      <Route
                        path="app/house-keeper/assets"
                        element={<HouseKeeperIHaveMyOwn />}
                      />

                      <Route
                        path="app/baby-sitter/have"
                        element={<BabySitterHave />}
                      />
                      <Route
                        path="app/baby-sitter/available-for"
                        element={<BabySitterAvailableFor />}
                      />
                      <Route
                        path="app/baby-sitter/happy-with"
                        element={<BabySitterHappyWith />}
                      />
                      <Route
                        path="app/baby-sitter/specialities"
                        element={<BabySitterSpecialties />}
                      />
                      <Route
                        path="app/baby-sitter/experience"
                        element={<BabySitterExperience />}
                      />
                      <Route
                        path="app/baby-sitter/children"
                        element={<BabySitterChildrence />}
                      />
                      <Route
                        path="app/help&support/categories"
                        element={<Categories />}
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  {loginUserDetails.managerPermission.includes(
                    __permissionsObj.bookingManagement
                  ) || loginUserDetails.role === "Admin" ? (
                    <>
                      <Route
                        path="booking/job-posted"
                        element={<JobPosted />}
                      />
                      <Route
                        path="booking/job-posted/appliers"
                        element={<JobAppliersListing />}
                      />
                      <Route path="booking/bookings" element={<Bookings />} />
                    </>
                  ) : (
                    <></>
                  )}

                  {loginUserDetails.managerPermission.includes(
                    __permissionsObj.RoleManagement
                  ) || loginUserDetails.role === "Admin" ? (
                    <>
                      <Route path="role" element={<Roles />} />
                      <Route path="role/sub-admin" element={<SubAdmin />} />
                    </>
                  ) : (
                    <></>
                  )}

                  <Route path="scope/available-cities" element={<Cities />} />
                  <Route path="scope/available-areas" element={<Areas />} />
                  <Route path="scope/nona-fees" element={<NonaFees />} />
                  <Route path="scope/promo-code" element={<Promocode />} />
                  <Route
                    path="general-settings/policy-page"
                    element={<PolicyPage />}
                  />
                  <Route
                    path="general-settings/terms-condition"
                    element={<TermsAndConditions />}
                  />
                  <Route
                    path="general-settings/disclaimer"
                    element={<Disclaimer />}
                  />
                  <Route
                    path="general-settings/cancel-policy"
                    element={<CancelPolicy />}
                  />
                  <Route path="faqs" element={<FAQs />} />
                  <Route path="faqs/customer" element={<CustomerFAQs />} />
                  <Route
                    path="faqs/housekeeper"
                    element={<HouseKeeperFAQs />}
                  />
                  <Route path="faqs/babysitter" element={<BabySitterFAQs />} />
                  <Route path="payments/user" element={<UserPayments />} />
                  <Route
                    path="payments/withdraw"
                    element={<WithdrawRequest />}
                  />
                  <Route path="/payments/disputes" element={<DisputesList />} />
                  <Route path="help&support" element={<HelpSupportList />} />
                  <Route path="reports" element={<ReportList />} />
                </Route>
              </Routes>
            ) : (
              <Routes>
                {/* Stripe Pages */}
                <Route path="stripe/onboard/return" element={<ReturnUrl />} />
                <Route path="stripe/onboard/refresh" element={<RefreshUrl />} />

                <Route
                  path="/admin/reset-password"
                  element={
                    <ResetPassword setLoginUserDetails={setLoginUserDetails} />
                  }
                />
                <Route
                  path="*"
                  element={<LoginPage getAdminProfile={getAdminProfile} />}
                />
              </Routes>
            )}
          </BrowserRouter>
        </MainState>
      </LocalizationProvider>
    </>
  );
}

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default App;
